/**
 * BaselineActuals — set baseline, record actuals, view variance, get AI advice.
 *
 * Workflow:
 *   1. User saves a project (Calculator tab).
 *   2. User sets the current calculation as the **baseline** (frozen).
 *   3. User logs **actuals** per phase / sub-phase / total over time.
 *   4. Variance is computed on the fly (cumulative actuals vs baseline).
 *   5. User can request **AI corrective-action advice** (Claude Sonnet 4.5).
 */
import React, { useEffect, useMemo, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import {
  Lock, Unlock, Plus, Trash2, Sparkles, Loader2, GitCompare, AlertTriangle, TrendingUp, TrendingDown,
  Upload, FileSpreadsheet, Wallet, RefreshCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import InfoTip from './InfoTip';
import PrintButton from './PrintButton';
import SCurves from './SCurves';

import { makeFmt } from './currency';
const fmtPct = (n) => `${n > 0 ? '+' : ''}${Number(n || 0).toFixed(1)}%`;

const STATUS_BADGE = {
  green: { color: 'bg-emerald-600 text-white',  label: 'On budget' },
  amber: { color: 'bg-amber-500 text-white',    label: 'Caution' },
  red:   { color: 'bg-red-600 text-white',      label: 'Over budget' },
  no_actuals: { color: 'bg-slate-300 text-slate-700', label: 'No actuals' },
};

export default function BaselineActuals({ calculation, savedProjectId, onSaveRequest, onRevisedBurn }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const projectId = savedProjectId;

  const [variance, setVariance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [advising, setAdvising] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [revisedBurn, setRevisedBurn] = useState(null);
  const [monthsElapsed, setMonthsElapsed] = useState(0);

  // Add-actual form
  const [lineKey, setLineKey] = useState('total');
  const [actCost, setActCost] = useState('');
  const [actHours, setActHours] = useState('');
  const [actNote, setActNote] = useState('');

  const refresh = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const res = await ConstructionAPI.getVariance(projectId);
      setVariance(res);
    } catch (err) {
      // 404 handled silently
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [projectId]);

  // All possible line keys for the add-actual selector
  const allLines = useMemo(() => {
    if (!calculation) return [{ k: 'total', label: 'Project total' }];
    const out = [{ k: 'total', label: 'Project total' }];
    (calculation.phases || []).forEach(p => {
      out.push({ k: `phase:${p.phase}`, label: p.phase_label });
      (p.wbs || []).forEach(w => out.push({ k: `subphase:${p.phase}:${w.key}`, label: `↳ ${p.phase_label}: ${w.label}` }));
    });
    return out;
  }, [calculation]);

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first.</CardContent></Card>;
  }
  if (!projectId) {
    return (
      <Card data-testid="baseline-actuals-tab">
        <CardContent className="p-8 text-center">
          <GitCompare className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-700 mb-1">Save the project to set a baseline and track actuals.</p>
          <Button onClick={onSaveRequest} className="bg-orange-600 hover:bg-orange-700 mt-2" data-testid="bla-save-btn">Save project</Button>
        </CardContent>
      </Card>
    );
  }

  const baseline = variance?.baseline;
  const advice = variance?.advice;
  const rows = variance?.rows || [];
  const summary = variance?.summary || {};
  const rawActuals = variance?.actuals || [];

  // Build a cumulative-actual array (length = project_duration_months) by
  // bucketing each actual entry into the month it was recorded.
  const actualOverlay = useMemo(() => {
    if (!calculation || !rawActuals.length) return null;
    const months = Math.max(1, Math.round(calculation.project_duration_months || 12));
    // Use the earliest actual recorded_at as month-1 anchor.
    const stamps = rawActuals
      .map(a => a.recorded_at || a.created_at)
      .filter(Boolean)
      .map(s => new Date(s).getTime())
      .filter(t => !isNaN(t));
    if (!stamps.length) return null;
    const t0 = Math.min(...stamps);
    const monthly = Array(months).fill(0);
    rawActuals.forEach((a) => {
      const ts = new Date(a.recorded_at || a.created_at).getTime();
      if (isNaN(ts)) return;
      const monthIdx = Math.min(months - 1, Math.max(0, Math.floor((ts - t0) / (1000 * 60 * 60 * 24 * 30.4))));
      // Only count "total" + "phase:*" actuals to avoid double-counting sub-phase rollups
      if (a.line_key === 'total' || (a.line_key || '').startsWith('phase:')) {
        monthly[monthIdx] += Number(a.cost_usd || 0);
      }
    });
    // Cumulative
    const cumulative = [];
    let s = 0;
    for (const v of monthly) { s += v; cumulative.push(s); }
    return { cumulative_actual: cumulative };
  }, [calculation, rawActuals]);

  const setBaseline = async () => {
    try {
      await ConstructionAPI.setBaseline(projectId, calculation);
      toast.success('Baseline locked');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Set baseline failed'); }
  };
  const clearBaseline = async () => {
    if (!window.confirm('Clear baseline and all actuals? This cannot be undone.')) return;
    try {
      await ConstructionAPI.clearBaseline(projectId);
      toast.success('Baseline cleared');
      refresh();
    } catch (err) { toast.error('Clear failed'); }
  };
  const addActual = async () => {
    if (!actCost || Number(actCost) === 0) { toast.error('Enter an actual cost'); return; }
    const sel = allLines.find(l => l.k === lineKey);
    try {
      await ConstructionAPI.addActual(projectId, {
        line_key: lineKey,
        line_label: sel?.label,
        cost_usd: Number(actCost) || 0,
        hours: Number(actHours) || 0,
        note: actNote,
      });
      setActCost(''); setActHours(''); setActNote('');
      toast.success('Actual recorded');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Record failed'); }
  };
  const removeActual = async (id) => {
    try { await ConstructionAPI.removeActual(projectId, id); refresh(); } catch { toast.error('Delete failed'); }
  };
  const requestAdvice = async () => {
    try {
      setAdvising(true);
      const res = await ConstructionAPI.adviseVariance(projectId);
      if (res?.ok === false) { toast.error(res.error || 'Advice unavailable'); return; }
      toast.success('AI corrective actions ready');
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Advice failed'); }
    finally { setAdvising(false); }
  };

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('CSV too large (max 5 MB)'); return; }
    try {
      setCsvUploading(true);
      const fd = new FormData();
      fd.append('file', f);
      const res = await ConstructionAPI.uploadActualsCsv(projectId, fd);
      setCsvResult(res);
      if (res.imported > 0) {
        toast.success(`Imported ${res.imported} actuals${res.errors?.length ? ` · ${res.errors.length} errors` : ''}`);
        refresh();
      } else {
        toast.error(res.error || `No rows imported${res.errors?.length ? ` · ${res.errors.length} errors` : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV upload failed');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const recomputeBurn = async (curve = 's_curve') => {
    try {
      const res = await ConstructionAPI.computeRevisedBurn(projectId, {
        months_elapsed: Number(monthsElapsed) || 0, curve,
      });
      if (res?.ok === false) { toast.error(res.error || 'Cannot compute burn'); return; }
      setRevisedBurn(res);
      toast.success(`Revised monthly burn: ${fmt$(res.revised_monthly_burn_usd)}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const pushToFunding = () => {
    if (!revisedBurn) return;
    onRevisedBurn?.(revisedBurn);
    toast.success('Pushed to Funding tab');
  };

  const chartData = rows.filter(r => r.line_key !== 'total' && r.line_key.startsWith('phase:')).map(r => ({
    name: r.label.split(' ').slice(0, 2).join(' '),
    Baseline: r.baseline_cost_usd,
    Actual: r.actual_cost_usd,
    Variance: r.cost_variance_usd,
    status: r.status,
  }));

  return (
    <div className="space-y-4 print-section" data-testid="baseline-actuals-tab">
      {/* Header / actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold inline-flex items-center gap-1.5">
            <GitCompare className="h-4 w-4" />Baseline &amp; Actuals
            <InfoTip>Lock a snapshot of your calculation as the baseline, then record actuals over time. Variance is computed automatically.</InfoTip>
          </h3>
          {baseline ? <Badge className="bg-emerald-700 text-white"><Lock className="h-3 w-3 mr-1 inline" />Baseline locked</Badge>
                    : <Badge variant="outline">No baseline</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {!baseline && <Button onClick={setBaseline} className="bg-orange-600 hover:bg-orange-700" data-testid="bla-set-baseline-btn"><Lock className="h-3.5 w-3.5 mr-1.5" />Lock as baseline</Button>}
          {baseline  && <Button variant="outline" onClick={clearBaseline} data-testid="bla-clear-baseline-btn"><Unlock className="h-3.5 w-3.5 mr-1.5" />Clear</Button>}
          <PrintButton testid="bla-print-btn" />
        </div>
      </div>

      {!baseline && (
        <Card><CardContent className="p-4 text-sm text-slate-600">
          Lock the current calculation as your project baseline to start tracking actuals.
        </CardContent></Card>
      )}

      {baseline && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Pill accent="bg-slate-900" label="Baseline total" value={fmt$(summary.baseline_cost_usd)} />
            <Pill accent="bg-orange-600" label="Actual to date" value={fmt$(summary.actual_cost_usd)} />
            <Pill accent={(summary.cost_variance_usd ?? 0) > 0 ? 'bg-rose-600' : 'bg-emerald-700'}
                  label="Variance ($)"
                  value={fmt$(summary.cost_variance_usd)}
                  icon={(summary.cost_variance_usd ?? 0) > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />} />
            <Pill accent={(summary.cost_variance_pct ?? 0) > 0 ? 'bg-rose-600' : 'bg-emerald-700'}
                  label="Variance (%)" value={fmtPct(summary.cost_variance_pct)} />
            <Pill accent="bg-indigo-600" label="Status"
                  value={<Badge className={`${STATUS_BADGE[summary.status || 'no_actuals'].color} text-xs`}>{STATUS_BADGE[summary.status || 'no_actuals'].label}</Badge>} />
          </div>

          {/* Variance bar chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Phase-level variance</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={v => fmt$(v, { compact: true })} />
                    <Tooltip formatter={v => fmt$(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Baseline" fill="#0ea5e9" />
                    <Bar dataKey="Actual">
                      {chartData.map((d, i) => (
                        <Cell key={i} fill={d.status === 'red' ? '#ef4444' : d.status === 'amber' ? '#f59e0b' : '#22c55e'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Add actual form */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base inline-flex items-center gap-1.5"><Plus className="h-4 w-4" />Record an actual</CardTitle>
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-orange-700 hover:text-orange-800">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md border border-orange-300 bg-orange-50 ${csvUploading ? 'opacity-60' : ''}`} data-testid="bla-csv-label">
                    {csvUploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                    {csvUploading ? 'Uploading…' : 'Bulk-upload CSV'}
                  </span>
                  <input type="file" accept=".csv,text/csv" onChange={uploadCsv} className="hidden" disabled={csvUploading} data-testid="bla-csv-input" />
                </label>
              </div>
              <p className="text-[11px] text-slate-500">CSV columns: <code className="bg-slate-100 px-1">line_key,cost_usd,hours,period,note,line_label</code></p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                <div className="sm:col-span-4">
                  <label className="text-xs text-slate-600">Cost line</label>
                  <Select value={lineKey} onValueChange={setLineKey}>
                    <SelectTrigger className="h-9" data-testid="bla-line-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">{allLines.map(l => <SelectItem key={l.k} value={l.k}>{l.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-600">Cost (USD)</label>
                  <Input type="number" min="0" value={actCost} onChange={e => setActCost(e.target.value)} className="h-9" data-testid="bla-cost-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-600">Hours <span className="text-slate-400">opt.</span></label>
                  <Input type="number" min="0" value={actHours} onChange={e => setActHours(e.target.value)} className="h-9" data-testid="bla-hours-input" />
                </div>
                <div className="sm:col-span-3">
                  <label className="text-xs text-slate-600">Note</label>
                  <Input value={actNote} onChange={e => setActNote(e.target.value)} className="h-9" placeholder="e.g. May progress claim" data-testid="bla-note-input" />
                </div>
                <div className="sm:col-span-1">
                  <Button onClick={addActual} className="h-9 w-full bg-orange-600 hover:bg-orange-700" data-testid="bla-add-actual-btn"><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CSV import result */}
          {csvResult && (
            <Card data-testid="bla-csv-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm inline-flex items-center gap-1.5">
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" />CSV import
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700">
                  {csvResult.imported || 0} row{csvResult.imported === 1 ? '' : 's'} imported
                  {csvResult.errors?.length ? ` · ${csvResult.errors.length} skipped` : ''}.
                </p>
                {csvResult.errors?.length > 0 && (
                  <details className="mt-1 text-xs text-rose-700">
                    <summary className="cursor-pointer">View errors</summary>
                    <ul className="mt-1 ml-4 list-disc">
                      {csvResult.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                    </ul>
                  </details>
                )}
              </CardContent>
            </Card>
          )}

          {/* Revised burn from variance */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base inline-flex items-center gap-1.5">
                <Wallet className="h-4 w-4 text-indigo-600" />
                Revised monthly burn — from baseline minus actuals
                <InfoTip>Computes remaining cost (baseline − cumulative actuals), divides over remaining months and lets you push the new monthly plan straight into the Funding tab.</InfoTip>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <label className="text-xs text-slate-600">Months elapsed</label>
                  <Input type="number" min="0" value={monthsElapsed} onChange={e => setMonthsElapsed(e.target.value)} className="h-9 w-32" data-testid="bla-months-elapsed" />
                </div>
                <Select onValueChange={(v) => recomputeBurn(v)}>
                  <SelectTrigger className="h-9 w-44" data-testid="bla-curve-select"><SelectValue placeholder="Pick a curve…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="s_curve">S-curve</SelectItem>
                    <SelectItem value="linear">Linear</SelectItem>
                    <SelectItem value="front_loaded">Front-loaded</SelectItem>
                    <SelectItem value="back_loaded">Back-loaded</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => recomputeBurn('s_curve')} className="h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="bla-recompute-burn-btn">
                  <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />Recompute
                </Button>
                {revisedBurn && (
                  <Button onClick={pushToFunding} variant="outline" className="h-9" data-testid="bla-push-funding-btn">
                    <Wallet className="h-3.5 w-3.5 mr-1.5" />Apply to Funding tab
                  </Button>
                )}
              </div>
              {revisedBurn && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid="bla-revised-burn-result">
                  <BurnStat label="Remaining cost" value={fmt$(revisedBurn.remaining_cost_usd)} accent="bg-indigo-600" />
                  <BurnStat label="Remaining months" value={revisedBurn.months_remaining} accent="bg-sky-600" />
                  <BurnStat label="Revised burn / mo" value={fmt$(revisedBurn.revised_monthly_burn_usd)} accent="bg-emerald-700" />
                  <BurnStat label="Curve" value={revisedBurn.curve} accent="bg-slate-700" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Variance table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Variance — baseline vs actuals</CardTitle>
                <Button onClick={requestAdvice} disabled={advising} className="bg-purple-600 hover:bg-purple-700" data-testid="bla-advise-btn">
                  {advising ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Analysing…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />AI corrective actions</>}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Cost line</th>
                      <th className="text-right p-2">Baseline</th>
                      <th className="text-right p-2">Actual</th>
                      <th className="text-right p-2">Var ($)</th>
                      <th className="text-right p-2">Var (%)</th>
                      <th className="text-center p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const sb = STATUS_BADGE[r.status || 'no_actuals'];
                      const isTotal = r.line_key === 'total';
                      const isPhase = r.line_key.startsWith('phase:');
                      return (
                        <tr key={r.line_key} className={`border-t hover:bg-slate-50 ${isTotal ? 'font-bold' : ''}`} data-testid={`var-row-${r.line_key.replace(/:/g, '-')}`}>
                          <td className={`p-2 ${isTotal ? 'text-slate-900' : isPhase ? 'font-semibold text-slate-800' : 'text-slate-600 pl-6'}`}>{r.label}</td>
                          <td className="text-right p-2">{fmt$(r.baseline_cost_usd)}</td>
                          <td className="text-right p-2">{fmt$(r.actual_cost_usd)}</td>
                          <td className={`text-right p-2 ${r.cost_variance_usd > 0 ? 'text-rose-700' : r.cost_variance_usd < 0 ? 'text-emerald-700' : ''}`}>{fmt$(r.cost_variance_usd)}</td>
                          <td className={`text-right p-2 ${r.cost_variance_pct > 0 ? 'text-rose-700' : r.cost_variance_pct < 0 ? 'text-emerald-700' : ''}`}>{fmtPct(r.cost_variance_pct)}</td>
                          <td className="text-center p-2"><Badge className={`${sb.color} text-[10px]`}>{sb.label}</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Recent actuals list */}
          {variance?.actuals?.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Recent actuals ({variance.actuals.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                  {variance.actuals.slice().reverse().map(a => (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 font-medium truncate">{a.line_label || a.line_key}</div>
                        <div className="text-[11px] text-slate-500">{a.period} · {fmt$(a.cost_usd)} · {a.hours ? `${a.hours} hrs · ` : ''}{a.note || 'no note'}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeActual(a.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI advice */}
          {advice?.ok && (
            <Card data-testid="variance-advice">
              <CardHeader className="pb-3">
                <CardTitle className="text-base inline-flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-purple-600" />AI corrective actions
                </CardTitle>
                <p className="text-xs text-slate-500">Generated by Claude Sonnet 4.5 · trend: <strong>{advice.trend}</strong> · confidence: {advice.confidence}</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {advice.executive_summary && <p className="text-sm text-slate-700">{advice.executive_summary}</p>}
                {advice.top_risks?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-1 inline-flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-600" />Top risks</h4>
                    <ul className="space-y-1 text-sm text-slate-700 ml-4 list-disc">
                      {advice.top_risks.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {advice.corrective_actions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-1.5">Recommended actions</h4>
                    <div className="space-y-2">
                      {advice.corrective_actions.map((a, i) => (
                        <div key={i} className="bg-purple-50 border border-purple-200 rounded-md p-2.5 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className="bg-purple-600 text-white text-[10px]">#{i + 1}</Badge>
                            <span className="font-semibold text-slate-800">{a.line || 'General'}</span>
                            <Badge variant="outline" className="text-[10px]">{a.owner_role || 'PM'}</Badge>
                            {a.due_in_weeks > 0 && <Badge variant="outline" className="text-[10px]">due {a.due_in_weeks}w</Badge>}
                            {a.expected_savings_usd > 0 && <Badge className="bg-emerald-600 text-white text-[10px]">save {fmt$(a.expected_savings_usd)}</Badge>}
                          </div>
                          <div className="text-slate-700 mt-1">{a.action}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Wave 9: Discipline-wise costs and S-curves with planned vs actual overlay */}
      {calculation?.scurve && (
        <SCurves
          scurve={calculation.scurve}
          phases={calculation.phases}
          subDisciplineSummary={calculation.sub_discipline_summary}
          actuals={actualOverlay}
          calculation={calculation}
        />
      )}
    </div>
  );
}

function BurnStat({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-2 flex items-center gap-2">
      <span className={`w-1.5 h-8 rounded ${accent}`} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}

function Pill({ accent, label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 stx-kpi-tile">
      <div className={`p-2 rounded-lg text-white ${accent}`}>{icon || <span className="block w-4 h-4" />}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate text-sm">{value}</div>
      </div>
    </div>
  );
}
